import {
  SqliteSearchAdapter,
  SqliteStorageAdapter,
  type SqliteExecutor,
} from '@littlebird/local-db';
import {
  NoOpSearchAdapter,
  type LittlebirdSDK,
  type Meeting,
  type SearchAdapter,
  type StorageAdapter,
} from '@littlebird/sdk';

import { isDev } from '@/env';
import { inElectron } from '@/utils';

import { logger, sdkLogger, sdkLogRing } from '../logger';

import { createElectronExecutor } from './electron-executor';
import { createSyncConsole, type SyncConsole } from './sync-console';
import { createWebExecutor } from './web-executor';

const SYNC_CHANNEL_NAME = 'littlebird-sync';
// Databases left behind by the IndexedDB storage and FlexSearch index that SQLite replaced
// eslint-disable-next-line littlebird/brand-spelling
const LEGACY_INDEXEDDB_NAMES = ['littlebird', 'Littlebird'];

interface CreateAdaptersOptions {
  skipSearchIndex: boolean;
  meetingNotesToText: (notes: NonNullable<Meeting['notes']>) => string;
  getSdk: () => LittlebirdSDK | undefined;
}

/** Other windows re-hydrate their stores when this one finishes applying a sync packet */
function createCrossWindowSync(getSdk: CreateAdaptersOptions['getSdk']) {
  const channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
    if (event.data?.type !== 'data-synced') return;
    getSdk()
      ?.refreshFromExternalStorageSync()
      .catch((error: unknown) =>
        logger.error('Failed to refresh stores after sync broadcast', { error })
      );
  };
  return () => channel.postMessage({ type: 'data-synced' });
}

/** Deleting a database that does not exist succeeds immediately, so this is safe to run every launch */
function deleteLegacyIndexedDbs(): void {
  try {
    for (const name of LEGACY_INDEXEDDB_NAMES) indexedDB.deleteDatabase(name);
  } catch {
    // IndexedDB can be unavailable (private mode); nothing to clean up then
  }
}

function exposeConsoleHook(
  storage: SqliteStorageAdapter,
  executor: SqliteExecutor
): void {
  const hook = {
    sql: async (text: string, params: unknown[] = []) =>
      (
        await executor.execute({
          sql: text,
          params: params as never[],
          method: 'all',
        })
      ).rows,
    diagnostics: () => storage.diagnostics(),
    export: async () => {
      if (!executor.exportFile)
        throw new Error('Electron: the file is at diagnostics().file');
      const bytes = await executor.exportFile();
      const url = URL.createObjectURL(
        new Blob([bytes as unknown as BlobPart], {
          type: 'application/x-sqlite3',
        })
      );
      const link = Object.assign(document.createElement('a'), {
        href: url,
        download: 'littlebird.sqlite3',
      });
      link.click();
      URL.revokeObjectURL(url);
      return bytes.byteLength;
    },
  };
  (globalThis as { __localDb?: typeof hook }).__localDb = hook;
}

export function createAdapters(options: CreateAdaptersOptions): {
  storageAdapter: StorageAdapter;
  searchAdapter: SearchAdapter;
  notifyCrossWindowSync: () => void;
} {
  deleteLegacyIndexedDbs();
  const notifyCrossWindowSync = createCrossWindowSync(options.getSdk);
  const executor = inElectron()
    ? createElectronExecutor()
    : createWebExecutor();
  const storageAdapter = new SqliteStorageAdapter(executor, logger, {
    onCrossWindowSync: notifyCrossWindowSync,
  });
  exposeConsoleHook(storageAdapter, executor);
  const syncConsole = createSyncConsole({
    getSdk: options.getSdk,
    executor,
    logger: sdkLogger,
    consoleAttached: isDev,
    ring: sdkLogRing,
  });
  (globalThis as { __sync?: SyncConsole }).__sync = syncConsole;
  if (syncConsole.isVerbosePersisted()) syncConsole.verbose(true);
  return {
    storageAdapter,
    searchAdapter: options.skipSearchIndex
      ? new NoOpSearchAdapter()
      : new SqliteSearchAdapter(storageAdapter, logger, {
          meetingNotesToText: options.meetingNotesToText,
          // File results are surfaced on mobile only; exclude before ranking so filename hits cannot crowd out other types
          excludeTypes: ['file'],
        }),
    notifyCrossWindowSync,
  };
}
